import { Component, inject, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Order } from '../../models/order.model';
import { OrderService } from 'src/app/shared/services/order.service';
import { OrderState } from 'src/app/shared/enums/order-state.enum';
import { ShipmentStateEnum } from "../../enums/shipment-state.enum";
import { ExternalProductionState } from "../../enums/external-production-state.enum";
import { WebProductService } from "../../services/web-product.service";
import { ShipmentService } from "../../services/shipment.service";
import { ExternalProductionService } from "../../services/external-production.service";
import { OrganizationService } from "../../services/organization.service";
import { WebShipmentService } from "../../services/web-shipment.service";
import { SubcontractRequestService } from "../../services/subcontract-request.service";
import { SubcontractRequestState } from "../../enums/subcontract-request-state.enum";
import { PlanningService } from "../../services/planning.service";

@Component({
    selector: 'app-unlock-order',
    templateUrl: './restore-order.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
/**
 * Create or update order popup component
 */
export class RestoreOrderComponent {
  protected readonly OrderState = OrderState;
  progress = false;
  error = null;
  selectedState: OrderState = OrderState.WAIT_FILES;
  private orderService = inject(OrderService);
  private webProductService = inject(WebProductService);
  private webShipmentService = inject(WebShipmentService);
  private shipmentService = inject(ShipmentService);
  private externalProductionService = inject(ExternalProductionService);
  private subcontractRequestService = inject(SubcontractRequestService);
  private organizationService = inject(OrganizationService);
  private planningService = inject(PlanningService);

  constructor(
    public dialogRef: MatDialogRef<RestoreOrderComponent>,
    @Inject(MAT_DIALOG_DATA) public order: Order) {
  }

  /**
   * Close popup without any actions
   */
  public cancel(): void {
    this.dialogRef.close(null);
  }

  /**
   * Update order state
   */
  async edit(): Promise<void> {
    this.startUpdate();
    this.order.state = this.selectedState;
    this.order.print = false;
    this.order.archived = false;
    this.order.batMailSent = false;
    const organizationId = this.organizationService.currentOrganization.id;
    if (this.order.webOrderId) {
      const webProductId = this.order.products[0].webProduct;
      const updatedData = this.order.state === OrderState.WAIT_FILES ?
        { state: this.order.state, filePath: '' } : { state: this.order.state };
      await this.webProductService.update(webProductId, updatedData);
    }
    const shipments = await this.shipmentService.getByOrderId(this.order.id);
    for (const shipment of shipments) {
      await this.shipmentService.update(shipment.id, { state: ShipmentStateEnum.TO_SHIP,
        shipDate: null, shipTime: null });
      if (shipment.webShipmentId) {
        await this.webShipmentService.update(shipment.webShipmentId, { state: ShipmentStateEnum.UNKNOWN })
      }
    }
    const externalProductions = await this.externalProductionService.getByOrderId(organizationId, this.order.id);
    for (const externalProduction of externalProductions) {
      await this.externalProductionService.update(externalProduction.id, {
        state: ExternalProductionState.NEW,
        subcontractorId: '',
        subcontractorEmails: [],
      });
      const requests = await this.subcontractRequestService
        .getByExternalProduction(externalProduction.organizationId, externalProduction.id);
      for (const request of requests) {
        await this.subcontractRequestService.update(request.id, { state: SubcontractRequestState.NEW });
      }
    }
    await this.orderService.update(this.order.id, {
      state: this.order.state,
      print: this.order.print,
      archived: this.order.archived,
      batMailSent: this.order.batMailSent
    });
    await this.orderService.createUpdateStateTask(this.order.organizationId, this.order.id, this.order.state);
    // Last: the operations only restart fresh once the order itself is restored
    await this.planningService.restoreOrderOperations(organizationId, this.order.id);
    this.dialogRef.close(null);
  }

  /**
   * Update process states
   */
  private startUpdate(): void {
    this.progress = true;
    this.error = null;
  }
}
